import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAxios, useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Plus, ChevronDown, ChevronUp, Edit, Image as ImageIcon, MessageCircle, ArrowRight, CheckCircle2, CircleDot, Circle, Save, X, Trash2, Upload, Camera, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Footer } from '../components/Footer';
import { ML, buildOrderedGroups } from '../constants/measurements';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildWhatsAppMessage(client, order) {
  let msg = `*Payal Garg - The Designer Studio*\n\nHi ${client.name}, here are your measurement details:\n\n`;
  msg += `*Order #${order.order_number}* - ${order.garment_type?.replace('_',' ')}\n`;
  const m = order.measurements || {};
  const groups = { Upper: [], Bottom: [], Blouse: [] };
  Object.entries(m).forEach(([k,v]) => {
    if (!v) return;
    const label = ML[k] || k;
    if (k.startsWith('upper_')) groups.Upper.push(`${label}: ${v}`);
    else if (k.startsWith('bottom_')) groups.Bottom.push(`${label}: ${v}`);
    else if (k.startsWith('blouse_')) groups.Blouse.push(`${label}: ${v}`);
  });
  Object.entries(groups).forEach(([g, items]) => {
    if (items.length) msg += `\n*${g}:*\n${items.join('\n')}\n`;
  });
  if (order.special_notes) msg += `\n*Notes:* ${order.special_notes}\n`;
  msg += `\n_SCO 324-325, Sector 38-D, Chandigarh_\n_Ph: 098767 94211_`;
  return msg;
}

function sendWhatsApp(phone, message) {
  let num = phone.replace(/\D/g, '');
  if (num.startsWith('0')) num = '91' + num.slice(1);
  if (!num.startsWith('91')) num = '91' + num;
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(message)}`, '_blank');
}

// Team WhatsApp — used for the "Send to Azaz" button
const TEAM_WA_NUMBER = '917814800703';
const TEAM_WA_NAME = 'Azaz';

function buildTeamMessage(client, order) {
  let msg = `*Payal Garg - The Designer Studio*\n*Order Forward*\n\n`;
  msg += `*Client:* ${client.name}\n*Phone:* ${client.phone}\n*Client #:* ${client.client_number}\n\n`;
  msg += `*Order #${order.order_number}* — ${order.garment_type?.replace('_', ' ')}\n*Status:* ${order.status?.replace('_', ' ')}\n`;
  const m = order.measurements || {};
  const groups = { Upper: [], Bottom: [], Blouse: [] };
  Object.entries(m).forEach(([k, v]) => {
    if (!v) return;
    const label = ML[k] || k;
    if (k.startsWith('upper_')) groups.Upper.push(`${label}: ${v}`);
    else if (k.startsWith('bottom_')) groups.Bottom.push(`${label}: ${v}`);
    else if (k.startsWith('blouse_')) groups.Blouse.push(`${label}: ${v}`);
  });
  Object.entries(groups).forEach(([g, items]) => {
    if (items.length) msg += `\n*${g}:*\n${items.join('\n')}\n`;
  });
  if (order.special_notes) msg += `\n*Notes:* ${order.special_notes}\n`;
  return msg;
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [activeImgUploadOrder, setActiveImgUploadOrder] = useState(null);
  const [imgUploading, setImgUploading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null); // {url, filename}
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [trialDialog, setTrialDialog] = useState(null);
  const [trialForm, setTrialForm] = useState({ notes: '', measurements: {} });
  const [submitting, setSubmitting] = useState(false);
  // Edit mode state
  const [editOrderId, setEditOrderId] = useState(null);
  const [editForm, setEditForm] = useState({ measurements: {}, special_notes: '' });

  const STATUS_FLOW = ['new', 'in_progress', 'trial', 'completed', 'delivered'];
  const STATUS_LABELS = { new: 'New', in_progress: 'In Progress', trial: 'Trial', completed: 'Completed', delivered: 'Delivered' };
  const STATUS_COLORS = {
    new: { bg: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-50' },
    in_progress: { bg: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50' },
    trial: { bg: 'bg-purple-500', text: 'text-purple-700', light: 'bg-purple-50' },
    completed: { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-50' },
    delivered: { bg: 'bg-[#1C1C1E]', text: 'text-[#1C1C1E]', light: 'bg-[#1C1C1E]/10' },
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await authAxios.put(`${API}/orders/${orderId}`, { status: newStatus });
      fetchClient();
    } catch (err) { console.error('Status update failed:', err); }
  };

  const getNextStatus = (current) => {
    const idx = STATUS_FLOW.indexOf(current);
    return idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
  };

  useEffect(() => { fetchClient(); }, [id]);

  const fetchClient = async () => {
    try {
      const { data } = await authAxios.get(`${API}/clients/${id}`);
      setClientData(data);
      if (data.orders?.length > 0) setExpandedOrder(data.orders[0].id);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleAddTrial = async () => {
    if (!trialDialog) return;
    setSubmitting(true);
    try {
      await authAxios.post(`${API}/orders/${trialDialog}/trial`, { notes: trialForm.notes, measurement_updates: trialForm.measurements });
      setTrialDialog(null); setTrialForm({ notes: '', measurements: {} }); fetchClient();
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const statusColor = (s) => ({ new:'bg-blue-50 text-blue-700', in_progress:'bg-amber-50 text-amber-700', trial:'bg-purple-50 text-purple-700', completed:'bg-green-50 text-green-700', delivered:'bg-[#1C1C1E]/10 text-[#1C1C1E]' }[s] || 'bg-gray-50 text-gray-700');

  const startEdit = (order) => {
    setEditOrderId(order.id);
    setEditForm({
      measurements: { ...(order.measurements || {}) },
      special_notes: order.special_notes || '',
    });
  };

  const cancelEdit = () => {
    setEditOrderId(null);
    setEditForm({ measurements: {}, special_notes: '' });
  };

  const saveEdit = async () => {
    if (!editOrderId) return;
    setSubmitting(true);
    try {
      // Strip empty string values so DB stays clean — order matters, keep them undefined
      const cleaned = {};
      Object.entries(editForm.measurements).forEach(([k, v]) => {
        const s = String(v ?? '').trim();
        if (s !== '') cleaned[k] = s;
      });
      await authAxios.put(`${API}/orders/${editOrderId}`, {
        measurements: cleaned,
        special_notes: editForm.special_notes || '',
      });
      toast.success('Measurements updated');
      cancelEdit();
      fetchClient();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setSubmitting(false);
  };

  // Add a reference image to an existing order
  const addImageToOrder = async (order, fileList) => {
    if (!fileList || fileList.length === 0) return;
    setImgUploading(true);
    try {
      const newImages = [...(order.reference_images || [])];
      for (const file of fileList) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await authAxios.post(`${API}/upload`, fd);
        newImages.push({ path: data.path, filename: data.filename, content_type: data.content_type });
      }
      await authAxios.put(`${API}/orders/${order.id}`, { reference_images: newImages });
      toast.success(`${fileList.length} image${fileList.length > 1 ? 's' : ''} added`);
      fetchClient();
    } catch (err) {
      toast.error('Image upload failed');
    }
    setImgUploading(false);
    setActiveImgUploadOrder(null);
  };

  const removeImageFromOrder = async (order, index) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const updated = (order.reference_images || []).filter((_, i) => i !== index);
      await authAxios.put(`${API}/orders/${order.id}`, { reference_images: updated });
      toast.success('Image removed');
      fetchClient();
    } catch {
      toast.error('Failed to remove image');
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Permanently delete Order #${order.order_number}? This cannot be undone.`)) return;
    try {
      await authAxios.delete(`${API}/orders/${order.id}`);
      toast.success(`Order #${order.order_number} deleted`);
      fetchClient();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete order');
    }
  };

  const deleteClient = async () => {
    if (!clientData?.client) return;
    const { client, orders } = clientData;
    const orderText = orders.length > 0 ? ` and all ${orders.length} order(s)` : '';
    if (!window.confirm(`Permanently delete ${client.name}${orderText}? This cannot be undone.`)) return;
    if (!window.confirm('Are you absolutely sure? Type-equivalent confirmation.')) return;
    try {
      await authAxios.delete(`${API}/clients/${client.id}`);
      toast.success(`${client.name} deleted`);
      navigate('/admin/clients');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete client');
    }
  };

  // Always render every box in the predefined sequence.
  const renderMeasurements = (m, orderId) => {
    const isEditing = editOrderId === orderId;
    const source = isEditing ? editForm.measurements : (m || {});
    const groups = buildOrderedGroups(source);

    return (
      <div className="space-y-4">
        {groups.map(({ group, items }) => (
          <div key={group}>
            <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-[#8E8E93] mb-2">{group}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
              {items.map(({ k, label, v }) => (
                <div key={k} className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-2xl border ${v ? 'bg-[#F5F5F5] border-transparent' : 'bg-white border-dashed border-[#E5E5EA]'}`}>
                  <span className="text-[10px] sm:text-xs text-[#8E8E93] whitespace-nowrap">{label}</span>
                  {isEditing ? (
                    <Input
                      data-testid={`edit-m-${k}`}
                      value={editForm.measurements[k] || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, measurements: { ...f.measurements, [k]: e.target.value } }))}
                      placeholder="--"
                      className="h-7 w-14 sm:w-16 text-xs text-right border-[#E5E5EA] rounded-lg px-1.5 bg-white"
                    />
                  ) : (
                    <span className={`text-xs sm:text-sm font-medium ${v ? 'text-[#1C1C1E]' : 'text-[#C7C7CC]'}`}>{v || '—'}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center"><p className="text-[#8E8E93]">Loading...</p></div>;
  if (!clientData) return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center"><p className="text-[#8E8E93]">Client not found</p></div>;

  const { client, orders } = clientData;

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      {/* Hidden file inputs for adding images to existing orders */}
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => {
          const order = orders.find(o => o.id === activeImgUploadOrder);
          if (order) addImageToOrder(order, Array.from(e.target.files || []));
          e.target.value = '';
        }} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => {
          const order = orders.find(o => o.id === activeImgUploadOrder);
          if (order) addImageToOrder(order, Array.from(e.target.files || []));
          e.target.value = '';
        }} />
      <header className="bg-white border-b border-[#E5E5EA] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-[#8E8E93] h-8 w-8 p-0" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <span className="font-sans font-bold text-base sm:text-lg text-[#1C1C1E]" style={{ fontFamily: "'DM Sans', sans-serif" }}>Client</span>
          </div>
          <Button data-testid="add-order-for-client-btn" className="h-8 px-3 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-2xl text-xs" onClick={() => navigate(`/order/new/${client.id}`)}>
            <Plus className="w-3 h-3 mr-1" />New Order
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-5 sm:py-8 flex-1 w-full">
        {/* Client Info */}
        <Card className="border-[#E5E5EA] bg-white rounded-2xl shadow-sm mb-4 sm:mb-6">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#1C1C1E]/10 flex items-center justify-center shrink-0">
                <span className="text-lg sm:text-xl font-semibold text-[#1C1C1E]">{client.name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-2xl font-sans font-bold text-[#1C1C1E] truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{client.name}</h2>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-xs sm:text-sm text-[#8E8E93]">
                  <span>{client.phone}</span>
                  <span>#{client.client_number}</span>
                  <Badge className={`${client.gender==='male'?'bg-blue-50 text-blue-700':'bg-pink-50 text-pink-700'} border-0 text-[10px] sm:text-xs`}>{client.gender}</Badge>
                </div>
              </div>
              {isAdmin && (
                <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50 rounded-2xl h-8 px-2.5 text-xs shrink-0"
                  data-testid="delete-client-btn"
                  onClick={deleteClient}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  <span className="hidden sm:inline">Delete Client</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Orders */}
        <h3 className="text-base sm:text-xl font-sans font-bold text-[#1C1C1E] mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Orders ({orders.length})</h3>

        {orders.length === 0 ? (
          <Card className="border-[#E5E5EA] bg-white rounded-2xl text-center p-6"><p className="text-sm text-[#8E8E93]">No orders yet</p></Card>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <Card key={order.id} className="border-[#E5E5EA] bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-3 sm:p-4 flex items-center justify-between cursor-pointer hover:bg-[#F5F5F5]/50 transition-colors" onClick={() => setExpandedOrder(expandedOrder===order.id?null:order.id)} data-testid={`order-toggle-${order.order_number}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm sm:text-base text-[#1C1C1E]">#{order.order_number}</span>
                      <Badge className={`${statusColor(order.status)} border-0 text-[10px] capitalize`}>{order.status?.replace('_',' ')}</Badge>
                    </div>
                    <p className="text-xs text-[#8E8E93] mt-0.5">{order.garment_type?.replace('_',' ')} &middot; {new Date(order.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* WhatsApp to client */}
                    <Button variant="outline" size="sm" className="border-green-200 text-green-600 hover:bg-green-50 rounded-2xl h-7 sm:h-8 px-2 text-xs"
                      data-testid={`whatsapp-${order.order_number}`}
                      onClick={(e) => { e.stopPropagation(); sendWhatsApp(client.phone, buildWhatsAppMessage(client, order)); }}>
                      <MessageCircle className="w-3 h-3 mr-0.5" />
                      <span className="hidden sm:inline">Client</span>
                    </Button>
                    {/* Send to team (Azaz) */}
                    <Button variant="outline" size="sm" className="border-[#1C1C1E]/20 text-[#1C1C1E] hover:bg-[#F5F5F5] rounded-2xl h-7 sm:h-8 px-2 text-xs"
                      data-testid={`send-team-${order.order_number}`}
                      onClick={(e) => { e.stopPropagation(); sendWhatsApp(TEAM_WA_NUMBER, buildTeamMessage(client, order)); }}>
                      <Send className="w-3 h-3 mr-0.5" />
                      <span className="hidden sm:inline">{TEAM_WA_NAME}</span>
                    </Button>
                    <Button variant="outline" size="sm" className="border-[#E5E5EA] text-[#1C1C1E] rounded-2xl h-7 sm:h-8 px-2 text-xs"
                      data-testid={`add-trial-${order.order_number}`}
                      onClick={(e) => { e.stopPropagation(); setTrialDialog(order.id); setTrialForm({ notes:'', measurements:{} }); }}>
                      <Edit className="w-3 h-3 mr-0.5" />Trial
                    </Button>
                    {isAdmin && (
                      <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50 rounded-2xl h-7 sm:h-8 px-2 text-xs"
                        data-testid={`delete-order-${order.order_number}`}
                        onClick={(e) => { e.stopPropagation(); deleteOrder(order); }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                    {expandedOrder===order.id ? <ChevronUp className="w-4 h-4 text-[#8E8E93]"/> : <ChevronDown className="w-4 h-4 text-[#8E8E93]"/>}
                  </div>
                </div>

                {expandedOrder===order.id && (
                  <div className="border-t border-[#E5E5EA] p-3 sm:p-4">
                    {/* Status Workflow */}
                    <div className="mb-4 p-3 bg-[#F5F5F5] rounded-2xl">
                      <div className="flex items-center justify-between mb-2.5">
                        <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-[#8E8E93]">Order Status</p>
                        {getNextStatus(order.status) && (
                          <Button
                            data-testid={`status-advance-${order.order_number}`}
                            className="h-7 sm:h-8 px-2.5 sm:px-3 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-2xl text-[10px] sm:text-xs font-medium"
                            onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.id, getNextStatus(order.status)); }}
                          >
                            Mark as {STATUS_LABELS[getNextStatus(order.status)]}
                            <ArrowRight className="w-3 h-3 ml-1" strokeWidth={2} />
                          </Button>
                        )}
                      </div>
                      {/* Progress Steps */}
                      <div className="flex items-center gap-0 overflow-x-auto">
                        {STATUS_FLOW.map((s, i) => {
                          const currentIdx = STATUS_FLOW.indexOf(order.status);
                          const isDone = i < currentIdx;
                          const isCurrent = i === currentIdx;
                          const isFuture = i > currentIdx;
                          const colors = STATUS_COLORS[s];
                          return (
                            <div key={s} className="flex items-center">
                              <button
                                data-testid={`status-set-${order.order_number}-${s}`}
                                className={`flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-2xl text-[9px] sm:text-[11px] font-medium transition-all whitespace-nowrap ${
                                  isCurrent ? `${colors.light} ${colors.text} ring-1 ring-current` :
                                  isDone ? `${colors.light} ${colors.text} opacity-80` :
                                  'bg-transparent text-[#8E8E93]/50'
                                }`}
                                onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.id, s); }}
                              >
                                {isDone ? <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> :
                                 isCurrent ? <CircleDot className="w-3 h-3" strokeWidth={2} /> :
                                 <Circle className="w-3 h-3" strokeWidth={1.5} />}
                                <span className="hidden sm:inline">{STATUS_LABELS[s]}</span>
                              </button>
                              {i < STATUS_FLOW.length - 1 && (
                                <div className={`w-3 sm:w-5 h-px mx-0.5 ${isDone ? colors.bg : 'bg-[#E4E2DC]'}`} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <Tabs defaultValue="measurements">
                      <TabsList className="flex space-x-1 border-b border-[#E5E5EA] mb-3 bg-transparent p-0 h-auto overflow-x-auto">
                        {['measurements','images','trials'].map(tab=>(
                          <TabsTrigger key={tab} value={tab} className="px-2.5 py-1.5 text-xs sm:text-sm text-[#8E8E93] data-[state=active]:text-[#1C1C1E] data-[state=active]:border-b-2 data-[state=active]:border-[#C27185] data-[state=active]:bg-transparent data-[state=active]:shadow-sm rounded-none whitespace-nowrap">
                            {tab==='measurements'?'Measurements':tab==='images'?`Images (${order.reference_images?.length||0})`:`Trials (${order.trials?.length||0})`}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      <TabsContent value="measurements">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-[#8E8E93]">All Measurements</p>
                          {editOrderId === order.id ? (
                            <div className="flex gap-1.5">
                              <Button variant="outline" size="sm" className="h-7 px-2.5 border-[#E5E5EA] text-[#8E8E93] rounded-2xl text-[11px]"
                                data-testid={`cancel-edit-${order.order_number}`}
                                onClick={(e) => { e.stopPropagation(); cancelEdit(); }}>
                                <X className="w-3 h-3 mr-0.5" />Cancel
                              </Button>
                              <Button size="sm" className="h-7 px-2.5 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-2xl text-[11px]"
                                data-testid={`save-edit-${order.order_number}`}
                                onClick={(e) => { e.stopPropagation(); saveEdit(); }} disabled={submitting}>
                                <Save className="w-3 h-3 mr-0.5" />{submitting ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" className="h-7 px-2.5 border-[#E5E5EA] text-[#1C1C1E] rounded-2xl text-[11px]"
                              data-testid={`edit-order-${order.order_number}`}
                              onClick={(e) => { e.stopPropagation(); startEdit(order); }}>
                              <Edit className="w-3 h-3 mr-0.5" />Edit
                            </Button>
                          )}
                        </div>
                        {renderMeasurements(order.measurements, order.id)}
                        <div className="mt-3">
                          <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">Notes</p>
                          {editOrderId === order.id ? (
                            <Textarea
                              data-testid={`edit-notes-${order.order_number}`}
                              className="border-[#E5E5EA] rounded-2xl text-xs sm:text-sm min-h-[60px] bg-white"
                              placeholder="Any special instructions for this order..."
                              value={editForm.special_notes}
                              onChange={(e) => setEditForm(f => ({ ...f, special_notes: e.target.value }))}
                            />
                          ) : (
                            <div className={`p-2.5 rounded-2xl border ${order.special_notes ? 'bg-amber-500/10 border-amber-500/20' : 'bg-white border-dashed border-[#E5E5EA]'}`}>
                              <p className={`text-xs sm:text-sm ${order.special_notes ? 'text-[#1C1C1E]' : 'text-[#C7C7CC]'}`}>{order.special_notes || '— no notes yet —'}</p>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      <TabsContent value="images">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-[#8E8E93]">
                            Reference Images ({order.reference_images?.length || 0})
                          </p>
                          <div className="flex gap-1.5">
                            <Button variant="outline" size="sm" className="h-7 px-2.5 border-[#E5E5EA] text-[#1C1C1E] rounded-2xl text-[11px]"
                              data-testid={`img-gallery-${order.order_number}`}
                              disabled={imgUploading && activeImgUploadOrder === order.id}
                              onClick={(e) => { e.stopPropagation(); setActiveImgUploadOrder(order.id); galleryRef.current?.click(); }}>
                              <Upload className="w-3 h-3 mr-0.5" />Gallery
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 px-2.5 border-[#E5E5EA] text-[#1C1C1E] rounded-2xl text-[11px]"
                              data-testid={`img-camera-${order.order_number}`}
                              disabled={imgUploading && activeImgUploadOrder === order.id}
                              onClick={(e) => { e.stopPropagation(); setActiveImgUploadOrder(order.id); cameraRef.current?.click(); }}>
                              <Camera className="w-3 h-3 mr-0.5" />Camera
                            </Button>
                          </div>
                        </div>
                        {imgUploading && activeImgUploadOrder === order.id && (
                          <p className="text-[11px] text-[#8E8E93] mb-2">Uploading...</p>
                        )}
                        {order.reference_images?.length > 0 ? (
                          <div className="grid grid-cols-3 gap-2">
                            {order.reference_images.map((img,i)=>{
                              const path = img.path || img;
                              const url = `${API}/files/${path}?auth=${localStorage.getItem('access_token')}`;
                              return (
                                <div key={i} className="relative group aspect-square bg-[#F5F5F5] border border-[#E5E5EA] rounded-2xl overflow-hidden">
                                  <img src={url} alt="Ref"
                                    className="w-full h-full object-cover cursor-zoom-in"
                                    data-testid={`ref-img-${order.order_number}-${i}`}
                                    onClick={(e) => { e.stopPropagation(); setLightboxImage({ url, filename: img.filename || `image-${i+1}` }); }} />
                                  <button type="button" data-testid={`remove-img-${order.order_number}-${i}`}
                                    onClick={(e) => { e.stopPropagation(); removeImageFromOrder(order, i); }}
                                    className="absolute top-1 right-1 w-7 h-7 bg-white/95 rounded-full flex items-center justify-center shadow"
                                    style={{ opacity: 1 }}>
                                    <X className="w-3.5 h-3.5 text-[#1C1C1E]" strokeWidth={2} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : <div className="text-center py-6 border-2 border-dashed border-[#E5E5EA] rounded-2xl text-xs text-[#8E8E93]"><ImageIcon className="w-6 h-6 mx-auto mb-1 text-[#E4E2DC]" strokeWidth={1.5}/>No images yet — tap Gallery or Camera above to add</div>}
                      </TabsContent>
                      <TabsContent value="trials">
                        {order.trials?.length > 0 ? (
                          <div className="space-y-2">
                            {order.trials.map((t,i)=>(
                              <div key={i} className="p-2.5 bg-[#F5F5F5] border border-[#E5E5EA] rounded-2xl">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-[#1C1C1E]">Trial #{t.trial_number}</span>
                                  <span className="text-[10px] text-[#8E8E93]">{new Date(t.date).toLocaleDateString('en-IN')} by {t.updated_by_name}</span>
                                </div>
                                {t.notes && <p className="text-xs text-[#8E8E93] mb-1.5">{t.notes}</p>}
                                {Object.keys(t.measurement_updates||{}).length>0 && (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                                    {Object.entries(t.measurement_updates).map(([k,v])=>(
                                      <div key={k} className="flex justify-between text-[10px] bg-white px-2 py-1 rounded">
                                        <span className="text-[#8E8E93]">{ML[k]||k}</span>
                                        <span className="font-medium text-[#1C1C1E]">{v}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-xs text-[#8E8E93] text-center py-4">No trials yet</p>}
                      </TabsContent>
                    </Tabs>
                    <div className="mt-2 pt-2 border-t border-[#E5E5EA] flex items-center justify-between text-[10px] text-[#8E8E93]">
                      <span>By {order.created_by_name} &middot; {new Date(order.created_at).toLocaleDateString('en-IN')}</span>
                      {order.updated_by_name && <span>Updated: {order.updated_by_name}</span>}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <Footer />

      {/* Image Lightbox */}
      {lightboxImage && (
        <div data-testid="image-lightbox"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}>
          <button type="button" data-testid="lightbox-close"
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-white"
            onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }}>
            <X className="w-5 h-5" />
          </button>
          <img src={lightboxImage.url} alt={lightboxImage.filename}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Trial Dialog */}
      <Dialog open={!!trialDialog} onOpenChange={()=>setTrialDialog(null)}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-lg mx-auto">
          <DialogHeader>
            <DialogTitle className="font-sans font-bold text-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>Trial Update</DialogTitle>
            <DialogDescription className="text-xs text-[#8E8E93]">Enter updated measurements from this trial.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div>
              <Label className="text-xs font-semibold uppercase text-[#8E8E93] mb-1 block">Notes</Label>
              <Textarea data-testid="trial-notes-input" className="border-[#E5E5EA] focus:border-[#D1D1D6] rounded-2xl text-sm min-h-[60px]" placeholder="Trial notes..." value={trialForm.notes} onChange={(e)=>setTrialForm(f=>({...f,notes:e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-[#8E8E93] mb-1 block">Changed Measurements</Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                {Object.entries(ML).map(([k,l])=>(
                  <div key={k}>
                    <Label className="text-[10px] text-[#8E8E93]">{l}</Label>
                    <Input className="h-8 text-xs border-[#E5E5EA] rounded-2xl" placeholder="--" value={trialForm.measurements[k]||''} onChange={(e)=>{const v=e.target.value;setTrialForm(f=>{const m={...f.measurements};if(v)m[k]=v;else delete m[k];return{...f,measurements:m}})}} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 h-9 border-[#E5E5EA] rounded-2xl text-sm" onClick={()=>setTrialDialog(null)}>Cancel</Button>
              <Button data-testid="save-trial-btn" className="flex-1 h-9 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-2xl text-sm" onClick={handleAddTrial} disabled={submitting}>{submitting?'Saving...':'Save Trial'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
